import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Button from '../Button';
import { HiMenu, HiX } from 'react-icons/hi';
import { motion, AnimatePresence } from 'framer-motion';

const LogoContainer = styled(motion.div)`
  display: flex;
  align-items: center;
  cursor: pointer;
  contain: layout paint;
  will-change: transform;
`;

// const LogoText = styled.span`
//   font-family: ${props => props.theme.fonts.heading};
//   font-weight: ${props => props.theme.fontWeights.bold};
//   font-size: 1.5rem;
//   color: ${props => props.theme.colors.primary};
//   margin-left: 10px;
  
//   @media (max-width: 576px) {
//     font-size: 1.3rem;
//   }
// `;

const HeaderContainer = styled.header`
  background-color: ${props => props.theme.colors.grayLighter};
  padding: 1rem 0;
  position: fixed;
  width: 100%;
  top: 0;
  z-index: 1000;
  transition: all 0.3s ease;
  box-shadow: ${props => props.scrolled ? props.theme.boxShadow : 'none'};
  overflow: hidden;
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-align: left;
  width: 100%;
  max-width: 100%;
  height: 7rem;
`;

const Nav = styled(motion.nav)`
  display: flex;
  align-items: center;
  
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    position: fixed;
    top: 0;
    right: 0;
    height: 100vh;
    width: 80%;
    max-width: 350px;
    background-color: ${props => props.theme.colors.primary};
    z-index: 1001;
    padding: 2rem;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    box-shadow: -5px 0 15px rgba(0, 0, 0, 0.1);
    transform: translateX(${props => props.isOpen ? '0' : '100%'});
    transition: transform 0.3s ease-in-out;
  }
`;

const NavList = styled.ul`
  display: flex;
  list-style: none;
  margin: 0;
  align-items: center;
  
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
    margin-top: 3rem;
  }
`;

// eslint-disable-next-line
const NavItem = styled.li`
  margin: 0 1.2rem;
  
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    margin: 1rem 0;
    width: 100%;
  }
`;

// eslint-disable-next-line
const NavLink = styled.a`
  color: ${props => props.theme.colors.primary};
  font-family: ${props => props.theme.fonts.heading};
  font-weight: ${props => props.theme.fontWeights.bold};
  text-decoration: none;
  padding: 0.5rem;
  transition: all 0.3s ease;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    left: 0;
    bottom: 0;
    width: 0;
    height: 2px;
    background-color: ${props => props.theme.colors.primary};
    transition: width 0.3s ease;
  }
  
  &:hover {
    color: ${props => props.theme.colors.primary};
    
    &:after {
      width: 100%;
    }
  }
  
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    display: block;
    width: 100%;
    padding: 0.75rem 0;
    color: ${props => props.theme.colors.white};
    &:hover {
      color: ${props => props.theme.colors.grayLighter};
    }
  }
`;

const MobileMenuButton = styled(motion.button)`
  display: none;
  background: transparent;
  border: none;
  color: ${props => props.theme.colors.primary};
  font-size: 1.8rem;
  cursor: pointer;
  z-index: 1002;
  
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    display: block;
  }
`;

const ButtonContainer = styled.div`
  margin-left: 1.5rem;
  
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    margin-top: 2rem;
    margin-left: 0;
    width: 100%;
  }
`;

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: none;
  pointer-events: auto;
  
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    display: block;
  }
`;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 992);
    };
    
    checkIsMobile();
    
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    
    const handleResize = () => {
      checkIsMobile();
      if (window.innerWidth > 992 && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [isMenuOpen, isMobile]);
  
  const menuVariants = {
    open: {
      x: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 30
      }
    },
    closed: {
      x: '100%',
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 30
      }
    }
  };
  
  const itemVariants = {
    open: i => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: 0.1 + i * 0.1,
        duration: 0.3
      }
    }),
    closed: {
      opacity: 0,
      y: 20
    }
  };
  
  const closeMenu = () => setIsMenuOpen(false);
  
  const scrollToSection = (id) => {
    const target = document.getElementById(id);
    if (!target) return;
    const headerOffset = 80;
    const elementPosition = target.getBoundingClientRect().top + window.pageYOffset;
    const offsetPosition = elementPosition - headerOffset;
    window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
  };
  
  return (
    <HeaderContainer scrolled={scrolled}>
      <div className="container">
        <HeaderContent>
          <LogoContainer
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            onClick={() => window.scrollTo(0, 0)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <img src="/assets/image-removebg-preview.png" alt="LM-Consulting" width={120} />
            {/* <LogoText>LM Consulting</LogoText> */}
          </LogoContainer>
          
          <MobileMenuButton 
            onClick={toggleMenu}
            whileTap={{ scale: 0.9 }}
            aria-label={isMenuOpen ? 'Fechar menu' : 'Abrir menu'}
          >
            {isMenuOpen ? <HiX /> : <HiMenu />}
          </MobileMenuButton>
          
          <AnimatePresence>
            {isMenuOpen && (
              <Overlay
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={closeMenu}
              />
            )}
          </AnimatePresence>
          
          <Nav
            isOpen={isMenuOpen}
            variants={isMobile ? menuVariants : {}}
            initial={isMobile ? "closed" : false}
            animate={isMenuOpen ? 'open' : isMobile ? 'closed' : undefined}
          >
            <NavList>
              {[
                { name: 'Quem Somos', id: 'quem-somos' },
                { name: 'Serviços', id: 'servicos' },
                { name: 'Profissionais', id: 'profissionais' },
                { name: 'Blog', id: 'blog' },
                // { name: 'Contato', id: 'contato' }
              ].map((item, i) => (
                <ButtonContainer>
                  <motion.div
                    variants={itemVariants}
                    custom={4}
                    initial={isMobile ? "closed" : "open"}
                    animate={isMenuOpen || !isMobile ? 'open' : 'closed'}
                  >
                    <Button 
                      tertiary 
                      fullWidth={isMobile}
                      href={`#${item.id}`}
                      onClick={(e) => { e.preventDefault(); closeMenu(); scrollToSection(item.id); }}
                    >
                      {item.name}
                    </Button>
                  </motion.div>
                </ButtonContainer>
              ))}
            </NavList>
            {/* 
            <NavList>
              {[
                { name: 'Quem Somos', id: 'quem-somos' },
                { name: 'Serviços', id: 'servicos' },
                { name: 'Diferenciais', id: 'diferenciais' },
                { name: 'Contato', id: 'contato' }
              ].map((item, i) => (
                <NavItem key={i}>
                  <motion.div
                    custom={i}
                    variants={itemVariants}
                    initial={isMobile ? "closed" : "open"}
                    animate={isMenuOpen || !isMobile ? 'open' : 'closed'}
                  >
                    <NavLink 
                      href={`#${item.id}`}
                      onClick={(e) => { e.preventDefault(); closeMenu(); scrollToSection(item.id); }}
                    >
                      {item.name}
                    </NavLink>
                  </motion.div>
                </NavItem>
              ))}
            </NavList>
            */}
            
            <ButtonContainer>
              <motion.div
                variants={itemVariants}
                custom={4}
                initial={isMobile ? "closed" : "open"}
                animate={isMenuOpen || !isMobile ? 'open' : 'closed'}
              >
                <Button 
                  tertiary 
                  fullWidth={isMobile}
                  onClick={() => window.open('https://wa.me/5519998621928', '_blank')}
                >
                  <i className="fab fa-whatsapp" style={{ marginRight: '5px' }}></i> Fale Conosco
                </Button>
              </motion.div>
            </ButtonContainer>
          </Nav>
        </HeaderContent>
      </div>
    </HeaderContainer>
  );
};

export default Header;
